import React, { useMemo, useState } from 'react';
import { Search, Download, FileText, ShieldCheck, Ban, Trash2, Archive, ArchiveRestore, FilePlus2, Pencil, Printer, CheckCircle2, Activity, LogIn, LogOut, FileDown, User, ChevronRight, X, ArrowRight } from 'lucide-react';
import type { AppState, AccionAuditoria, AuditEvent } from '../types';
import { PageShell, glassLight, inputStyle, ghostButton, EmptyState, StatCard, usePagination, Pagination, useIsMobile, Modal, T } from '../ui';
import { csvCell, downloadText } from '../storage';
import { ETIQUETA_ACCION, diaDe, horaDe } from '../auditoria';

// ── CÓMO SE VE CADA ACCIÓN ──────────────────────────────────────────────────
// El color y el ícono los pone la acción, que ahora viene guardada en el
// evento. Los eventos viejos no la traen: para esos se deduce de la frase al
// leer la fila (ver `normalizarEvento` en auditoria.ts), así que aquí ya llega
// resuelta en todos los casos.
const ASPECTO: Record<AccionAuditoria, {
  color: string;
  soft: string;
  icono: React.ElementType;
}> = {
  crear: {
    color: T.ok,
    soft: T.okSoft,
    icono: FilePlus2
  },
  editar: {
    color: T.info,
    soft: T.infoSoft,
    icono: Pencil
  },
  eliminar: {
    color: T.danger,
    soft: T.dangerSoft,
    icono: Trash2
  },
  archivar: {
    color: T.warn,
    soft: T.warnSoft,
    icono: Archive
  },
  desarchivar: {
    color: T.info,
    soft: T.infoSoft,
    icono: ArchiveRestore
  },
  autorizar: {
    color: T.ok,
    soft: T.okSoft,
    icono: ShieldCheck
  },
  anular: {
    color: T.danger,
    soft: T.dangerSoft,
    icono: Ban
  },
  imprimir: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: Printer
  },
  exportar: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: FileDown
  },
  resolver: {
    color: T.info,
    soft: T.infoSoft,
    icono: CheckCircle2
  },
  entrar: {
    color: T.brand,
    soft: T.accentSoft,
    icono: LogIn
  },
  salir: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: LogOut
  },
  otro: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: Activity
  }
};

// Orden en que se ofrecen los filtros: primero lo que cambia datos, después lo
// que solo los consulta o mueve.
const ORDEN_ACCIONES: AccionAuditoria[] = ['crear', 'editar', 'archivar', 'desarchivar', 'autorizar', 'anular', 'eliminar', 'resolver', 'imprimir', 'exportar', 'entrar', 'salir', 'otro'];

// Iniciales para el redondelito del autor. Dos palabras como mucho: "José Raúl
// Peralta" → "JR".
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  return (partes[0][0] + (partes[1]?.[0] ?? '')).toUpperCase();
}

// ── DETALLE DE UN EVENTO ────────────────────────────────────────────────────
// Se abre al pulsar la fila. Responde a las cuatro preguntas de una auditoría:
// quién, qué hizo, sobre qué, y qué cambió exactamente.
function DetalleEvento({
  evento,
  onClose
}: {
  evento: AuditEvent;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const aspecto = ASPECTO[evento.accion];
  const Icono = aspecto.icono;
  const dato = (etiqueta: string, valor: React.ReactNode) => <div data-id="element-150" data-mp="3j9zr7nnsqlyr">
      <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 3
    }} data-id="element-151" data-mp="3jbkp9n9bkn1d">

        {etiqueta}
      </div>
      <div style={{
      fontSize: 13.5,
      fontWeight: 600,
      color: T.text,
      wordBreak: 'break-word'
    }} data-id="element-152" data-mp="ftkfq3o489ln">

        {valor}
      </div>
    </div>;
  return <Modal onClose={onClose} title="Detalle del evento" wide data-id="element-153" data-mp="5v3iioyl2yr5">
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 18
    }} data-id="element-154" data-mp="wtncecuzy5j">

        <span style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        borderRadius: 12,
        flexShrink: 0,
        background: aspecto.soft,
        border: `1px solid ${aspecto.color}33`,
        color: aspecto.color
      }} data-id="element-155" data-mp="2qmn67hxaam7k">

          <Icono size={18} data-id="element-156" data-mp="j3lvwdsbjadq" />
        </span>
        <div style={{
        minWidth: 0
      }} data-id="element-157" data-mp="j4n66g1ifsue">
          <div style={{
          fontSize: 15,
          fontWeight: 700,
          color: T.text,
          lineHeight: 1.35,
          wordBreak: 'break-word'
        }} data-id="element-158" data-mp="4eph7xvraz45">

            {evento.mensaje}
          </div>
          <div style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: aspecto.color,
          marginTop: 2
        }} data-id="element-159" data-mp="x2v00f392e1c">

            {ETIQUETA_ACCION[evento.accion]}
          </div>
        </div>
      </div>

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 14,
      padding: '14px 16px',
      borderRadius: 12,
      background: T.surfaceSubtle,
      marginBottom: 18
    }} data-id="element-160" data-mp="n6i73jfj447n">

        {dato('Quién', evento.usuario || 'Sin registrar')}
        {dato('Qué', ETIQUETA_ACCION[evento.accion])}
        {dato('Entidad', evento.entidad || '—')}
        {dato('Registro', evento.registro || '—')}
      </div>

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: 14,
      marginBottom: 18
    }} data-id="element-161" data-mp="1pwhzqyt9essj">

        {dato('Cuándo', evento.sello || '—')}
        {dato('Fecha exacta', evento.fechaISO ? new Date(evento.fechaISO).toLocaleString('es-DO') : 'No registrada')}
      </div>

      {/* ── QUÉ CAMBIÓ ── */}
      <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 8
    }} data-id="element-162" data-mp="1771q9lzljpzz">

        {evento.accion === 'crear' ? 'Con qué se creó' : 'Qué cambió'}
      </div>

      {evento.cambios.length === 0 ? <div style={{
      fontSize: 12.5,
      color: T.textMuted,
      padding: '14px 16px',
      borderRadius: 12,
      border: `1px dashed ${T.border}`,
      lineHeight: 1.5
    }} data-id="element-163" data-mp="sgkn9y7bcs0z">

          Este evento no guardó el detalle de los campos. Los eventos anteriores
          a esta versión del sistema solo tienen la frase; los nuevos sí lo
          traen.
        </div> : <div style={{
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      overflow: 'hidden'
    }} data-id="element-164" data-mp="2xalrq7gizkfc">

          {evento.cambios.map((c, i) => <div key={i} style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,150px) minmax(0,1fr) 16px minmax(0,1fr)',
        gap: isMobile ? 4 : 12,
        alignItems: 'center',
        padding: '11px 14px',
        borderTop: i === 0 ? 'none' : `1px solid ${T.border}`,
        background: i % 2 ? T.surfaceSubtle : 'transparent'
      }} data-id="element-165" data-mp="328vounlt8v3l" data-mpi={i}>

              <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.textSecondary
        }} data-id="element-166" data-mp="2jjyvqn25g8my">
                {c.campo}
              </span>

              {/* En un alta no hay valor anterior: se muestra solo con qué
                  nació, sin la flecha ni el hueco de "antes". */}
              {c.antes === '' ? <span style={{
          fontSize: 13,
          color: T.text,
          fontWeight: 600,
          gridColumn: isMobile ? 'auto' : '2 / -1',
          wordBreak: 'break-word'
        }} data-id="element-167" data-mp="1jctnpjkik7yh">
                  {c.despues}
                </span> : <>
                  <span style={{
            fontSize: 13,
            color: T.textMuted,
            textDecoration: 'line-through',
            wordBreak: 'break-word'
          }} data-id="element-168" data-mp="14l1lm99qcqep">
                    {c.antes}
                  </span>
                  <ArrowRight size={14} style={{
            color: T.textMuted,
            flexShrink: 0,
            display: isMobile ? 'none' : 'block'
          }} data-id="element-169" data-mp="vniwasp1zpx0" />

                  <span style={{
            fontSize: 13,
            color: T.text,
            fontWeight: 700,
            wordBreak: 'break-word'
          }} data-id="element-170" data-mp="lojn990dls9h">
                    {c.despues}
                  </span>
                </>}
            </div>)}
        </div>}

      <div style={{
      marginTop: 18,
      display: 'flex',
      justifyContent: 'flex-end'
    }} data-id="element-171" data-mp="1ovdvoczfdm8y">
        <button type="button" onClick={onClose} style={ghostButton} data-id="element-172" data-mp="1ttp3ki65tmoj">
          <X size={14} data-id="element-173" data-mp="1f6418qh3zrho" /> Cerrar
        </button>
      </div>
    </Modal>;
}
export function Auditoria({
  state
}: {
  state: AppState;
}) {
  const isMobile = useIsMobile();
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('todos');
  const [filtroUsuario, setFiltroUsuario] = useState('todos');
  const [abierto, setAbierto] = useState<AuditEvent | null>(null);

  // `state.audit` ya viene con el más reciente primero y normalizado: la
  // pantalla no tiene que saber de qué época es cada fila.
  const eventos: AuditEvent[] = useMemo(() => Array.isArray(state.audit) ? state.audit : [], [state.audit]);

  // Solo se ofrecen como filtro las acciones y los autores que aparecen de
  // verdad en el registro.
  const accionesPresentes = useMemo(() => {
    const vistas = new Set(eventos.map(e => e.accion));
    return ORDEN_ACCIONES.filter(a => vistas.has(a));
  }, [eventos]);
  const usuariosPresentes = useMemo(() => [...new Set(eventos.map(e => e.usuario).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')), [eventos]);
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return eventos.filter(e => filtroAccion === 'todos' || e.accion === filtroAccion).filter(e => filtroUsuario === 'todos' || e.usuario === filtroUsuario).filter(e => {
      if (!q) return true;
      // La búsqueda también entra en el detalle: así se puede buscar un valor
      // concreto ("L-317355") y encontrar el evento que lo cambió.
      const enCambios = e.cambios.some(c => `${c.campo} ${c.antes} ${c.despues}`.toLowerCase().includes(q));
      return `${e.completo} ${e.usuario} ${e.entidad} ${e.registro}`.toLowerCase().includes(q) || enCambios;
    });
  }, [eventos, busqueda, filtroAccion, filtroUsuario]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados);

  // Día más reciente del registro: sirve para destacar "lo de hoy" sin
  // depender de la fecha real del navegador, porque el sello no trae año.
  const diaReciente = eventos.length ? diaDe(eventos[0].sello) : '';
  const stats = useMemo(() => ({
    total: eventos.length,
    recientes: eventos.filter(e => diaDe(e.sello) === diaReciente).length,
    cambios: eventos.filter(e => ['crear', 'editar', 'eliminar', 'archivar', 'desarchivar'].includes(e.accion)).length
  }), [eventos, diaReciente]);
  const exportar = () => {
    // El detalle también se exporta: una línea por campo cambiado, repitiendo
    // los datos del evento. Un CSV con el detalle metido en una sola celda no
    // se puede filtrar ni sumar, que es para lo que se exporta.
    const filas: string[][] = [['Fecha', 'Usuario', 'Acción', 'Entidad', 'Registro', 'Detalle', 'Campo', 'Antes', 'Después']];
    for (const e of filtrados) {
      const base = [e.sello, e.usuario, ETIQUETA_ACCION[e.accion], e.entidad, e.registro, e.mensaje];
      if (!e.cambios.length) filas.push([...base, '', '', '']);else for (const c of e.cambios) filas.push([...base, c.campo, c.antes, c.despues]);
    }
    const csv = filas.map(f => f.map(csvCell).join(',')).join('\n');
    // BOM para que Excel abra bien los acentos.
    downloadText('auditoria.csv', '﻿' + csv, 'text/csv;charset=utf-8');
  };

  // Los eventos de la página se agrupan por día, conservando el orden.
  const grupos = useMemo(() => {
    const salida: {
      dia: string;
      items: AuditEvent[];
    }[] = [];
    for (const e of pageItems) {
      const dia = diaDe(e.sello);
      const ultimo = salida[salida.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.items.push(e);else salida.push({
        dia,
        items: [e]
      });
    }
    return salida;
  }, [pageItems]);
  return <PageShell title="Auditoría" sub="Quién hizo qué, cuándo y qué cambió exactamente" action={eventos.length > 0 ? <button onClick={exportar} style={ghostButton} data-id="element-175" data-mp="3ckjx73nij4xv">
            <Download size={14} data-id="element-176" data-mp="37ol8tg26pmxg" /> Exportar CSV
          </button> : undefined} data-id="element-174" data-mp="1gjroi3dzj31y">

      <style data-id="element-177" data-mp="3mcy5kf93j9t0">{`
        .aud-dia { position: sticky; top: 0; z-index: 2; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
        .aud-row { position: relative; display: grid; align-items: start; transition: background .15s; width: 100%; text-align: left; border: 0; background: transparent; font-family: inherit; cursor: pointer; }
        .aud-row:hover { background: ${T.surfaceHover}; }
        .aud-row:focus-visible { outline: 2px solid ${T.accent}; outline-offset: -2px; }
        /* La flecha solo se destaca en la fila donde esta el puntero: si se
           viera siempre, catorce flechas competirian con el texto. */
        .aud-row .aud-ir { opacity: .35; transition: opacity .15s, transform .15s; }
        .aud-row:hover .aud-ir { opacity: 1; transform: translateX(2px); }
        /* Riel vertical que une los marcadores. Se recorta en el primero y
           el ultimo de cada dia para que no sobresalga del grupo. */
        .aud-row::before { content: ''; position: absolute; left: var(--riel); top: 0; bottom: 0; width: 1px; background: ${T.border}; }
        .aud-row:first-of-type::before { top: 26px; }
        .aud-row:last-of-type::before { bottom: calc(100% - 26px); }
        .aud-mark { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0; }
      `}</style>

      {eventos.length === 0 ? <EmptyState title="Sin eventos registrados" hint="Las acciones y cambios del sistema aparecerán aquí en cuanto ocurran." icon={<FileText size={22} data-id="element-179" data-mp="3ty1okqnfbe6u" />} data-id="element-178" data-mp="3oy5tjgwpex3f" /> : <>
          <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0,1fr))',
        gap: 14,
        marginBottom: 22
      }} data-id="element-180" data-mp="1n4zlvnn8up4n">

            <StatCard label="Eventos" value={String(stats.total)} note="en el registro" data-id="element-181" data-mp="3o7qlgybbrvgm" />

            <StatCard label={diaReciente ? `Actividad del ${diaReciente}` : 'Último día'} value={String(stats.recientes)} note="eventos" accent={T.info} data-id="element-182" data-mp="3o5e7sxclo79d" />

            <StatCard label="Cambios de datos" value={String(stats.cambios)} note="altas, ediciones y archivos" accent={T.warn} data-id="element-183" data-mp="3f5r3o3shkncm" />

          </div>

          {/* ── FILTROS ── */}
          <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 14
      }} data-id="element-184" data-mp="3594jzibwc2c6">

            <div style={{
          position: 'relative',
          flex: '1 1 240px'
        }} data-id="element-185" data-mp="30gl9wvcf3pc4">

              <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-186" data-mp="2wh2lvqpauy52" />

              <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por texto, usuario o valor cambiado" value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-187" data-mp="2mhlhrdpp4sb7" />

            </div>

            {/* El filtro por autor solo aparece cuando hay más de uno: con un
               único usuario no filtra nada y solo ocupa sitio. */}
            {usuariosPresentes.length > 1 && <select style={{
          ...inputStyle,
          flex: '0 1 210px'
        }} value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)} data-id="element-188" data-mp="2hbotp1dhrh7o">

                <option value="todos" data-id="element-189" data-mp="1oqf7a4276iwt">Todos los usuarios</option>
                {usuariosPresentes.map(u => <option key={u} value={u} data-id="element-190" data-mp="1toqcg1hjs58p">
                    {u}
                  </option>)}
              </select>}
          </div>

          <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 18
      }} data-id="element-191" data-mp="1yl72x2c7yvoy">

            {[{
          id: 'todos',
          label: 'Todos',
          color: T.accent,
          soft: T.accentSoft
        }, ...accionesPresentes.map(a => ({
          id: a,
          label: ETIQUETA_ACCION[a],
          color: ASPECTO[a].color,
          soft: ASPECTO[a].soft
        }))].map(t => {
          const activo = filtroAccion === t.id;
          return <button key={t.id} type="button" onClick={() => setFiltroAccion(t.id)} style={{
            padding: '6px 13px',
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: activo ? t.color : T.textSecondary,
            background: activo ? t.soft : 'transparent',
            border: `1px solid ${activo ? `${t.color}44` : T.border}`,
            transition: 'all .15s'
          }} data-id="element-192" data-mp="xvlqtn1mh5cr">

                    {t.label}
                  </button>;
        })}
          </div>

          {filtrados.length === 0 ? <EmptyState title="Ningún evento coincide" hint="Prueba con otro texto o quita los filtros." icon={<Search size={22} data-id="element-194" data-mp="20c5gtciyqj3i" />} data-id="element-193" data-mp="25b8ak0tht2w6" /> : <div style={{
        ...glassLight,
        overflow: 'hidden'
      }} data-id="element-195" data-mp="20cyg5sfle6bm">

              {grupos.map(grupo => <div key={grupo.dia} data-id="element-196" data-mp="27umu7ttymi1r">
                  <div className="aud-dia" style={{
            padding: '10px 18px',
            background: 'rgba(255,252,248,.86)',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 10
          }} data-id="element-197" data-mp="27xr67ddo2i2z">

                    <span style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: T.text
            }} data-id="element-198" data-mp="2qmfqbx4bsjdp">

                      {grupo.dia || 'Sin fecha'}
                    </span>
                    <span style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-199" data-mp="2lv7der3sefvs">

                      {grupo.items.length}{' '}
                      {grupo.items.length === 1 ? 'evento' : 'eventos'}
                    </span>
                  </div>

                  {grupo.items.map((e, i) => {
            const aspecto = ASPECTO[e.accion];
            const Icono = aspecto.icono;
            const hora = horaDe(e.sello);
            return <button key={`${grupo.dia}-${i}`} type="button" className="aud-row" onClick={() => setAbierto(e)} title="Ver el detalle de este evento" style={{
              // El riel se alinea con el centro del marcador.
              ['--riel' as string]: isMobile ? '33px' : '89px',
              gridTemplateColumns: isMobile ? '18px 30px minmax(0,1fr) 16px' : '74px 30px minmax(0,1fr) 16px',
              gap: 14,
              padding: '13px 18px'
            }} data-id="element-200" data-mp="2fvas096r5fk5" data-mpi={i}>

                        {isMobile ? <span data-id="element-201" data-mp="2tmplj9yacz5g" /> : <span style={{
                fontSize: 12,
                color: T.textMuted,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                paddingTop: 7,
                textAlign: 'right'
              }} data-id="element-202" data-mp="2tmxyt2ybm6h6">

                            {hora || '—'}
                          </span>}

                        <span className="aud-mark" title={ETIQUETA_ACCION[e.accion]} style={{
                background: aspecto.soft,
                border: `1px solid ${aspecto.color}33`,
                color: aspecto.color
              }} data-id="element-203" data-mp="3twgtbqwkmb1l">

                          <Icono size={14} data-id="element-204" data-mp="ce4hdd0s9p8b" />
                        </span>

                        <div style={{
                minWidth: 0,
                paddingTop: 4
              }} data-id="element-205" data-mp="cg7gcmjwysp6">
                          <div style={{
                  fontSize: 13.5,
                  color: T.text,
                  lineHeight: 1.45,
                  fontWeight: 600,
                  wordBreak: 'break-word'
                }} data-id="element-206" data-mp="less9yuvr9gz">

                            {e.mensaje}
                          </div>
                          <div style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: T.textMuted,
                  display: 'flex',
                  gap: 7,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }} data-id="element-207" data-mp="2s61yugi2owpf">

                            {/* Quién lo hizo va primero: es lo que se busca al
                                abrir esta pantalla. */}
                            <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontWeight: 700,
                    color: e.usuario ? T.textSecondary : T.textMuted
                  }} data-id="element-208" data-mp="1e2ecilwgilh3">

                              {e.usuario ? <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: 0,
                      background: T.accentSoft,
                      color: T.brand
                    }} data-id="element-209" data-mp="1srago7ve7ecf">
                                  {iniciales(e.usuario)}
                                </span> : <User size={11} data-id="element-210" data-mp="3r9vqp3i76iza" />}
                              {e.usuario || 'Sin registrar'}
                            </span>
                            <span data-id="element-211" data-mp="3m984cv4wyqbe">·</span>
                            <span style={{
                    color: aspecto.color,
                    fontWeight: 700
                  }} data-id="element-212" data-mp="3ib0khrf5l8vz">
                              {ETIQUETA_ACCION[e.accion]}
                            </span>
                            <span data-id="element-213" data-mp="dwkdbwn4iiql">·</span>
                            <span data-id="element-214" data-mp="9tny8fwsl4re">{e.entidad}</span>
                            {e.cambios.length > 0 ? <>
                                <span data-id="element-215" data-mp="4vvvsryiu08y">·</span>
                                <span style={{
                      fontWeight: 700,
                      color: T.textSecondary
                    }} data-id="element-216" data-mp="4x6yspk573z0">
                                    {e.cambios.length}{' '}
                                    {e.cambios.length === 1 ? 'campo' : 'campos'}
                                  </span>
                              </> : null}
                            {isMobile && hora ? <>
                                <span data-id="element-217" data-mp="2i22jgbftumhz">·</span>
                                <span data-id="element-218" data-mp="2i10ynf90eln2">{hora}</span>
                              </> : null}
                          </div>
                        </div>

                        <ChevronRight className="aud-ir" size={15} style={{
                color: T.textMuted,
                marginTop: 9
              }} data-id="element-219" data-mp="28c991drf8dfb" />

                      </button>;
          })}
                </div>)}

              <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-220" data-mp="1yel27pgjjfgs">
                <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="eventos" data-id="element-221" data-mp="23cmv123rqt5g" />

              </div>
            </div>}
        </>}

      {abierto ? <DetalleEvento evento={abierto} onClose={() => setAbierto(null)} data-id="element-222" data-mp="1ucq40ttwpi78" /> : null}
    </PageShell>;
}